import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { AppDataGrid } from "../../../../../components/datagrid/AppDataGrid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { common, economic, input, reduce } from "../../../../../i18n";
import { tableStyle } from "../../../../emission/_components/data-table/EmissionDataTableStyle";
import { EcoEfficiency } from "../../../../../models/dto/response/ResponseEcoEfficiency";
import { EconomicIndicatorComponent } from "../../../../../models/dto/response/ResponseCreateEconomicIndicators";
import { EcoEfficiencyContext } from "../../../../../contexts/EcoEfficiencyContext";
import NumberFormatterUtils from "../../../../../utils/numberFormatterUtils";
import LabelInput from "../../../../../components/input/LabelInput";

interface Props {
  editData: EcoEfficiency | undefined;
  fiveYearForecast: number[];
}

export const PlanSettingNormalizationTable = ({
  editData,
  fiveYearForecast,
}: Props): JSX.Element => {
  const { t, i18n } = useTranslation([common, input, economic, reduce], {
    nsMode: "fallback",
  });

  const { planSettingNormalization } = EcoEfficiencyContext();

  const [rows, setRows] = useState<EconomicIndicatorComponent[]>([]);

  const columns: GridColDef<EconomicIndicatorComponent>[] = [
    {
      field: "id",
      headerName: t("ID"),
      width: 80,
      sortable: false,
      filterable: false,
      colSpan: (params, row) => (row._id === "TOTAL" ? 2 : 1),
      renderCell: (params) => {
        return params.row._id !== "TOTAL"
          ? `EI-${params.api.getRowIndexRelativeToVisibleRows(params.id) + 1}`
          : `${t("Emission")} (kgCO₂e)`;
      },
    },
    {
      field: "nameEn",
      headerName: t("Components"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1,
      valueGetter: (value, row) =>
        i18n.language === "th" ? row.nameTh : row.nameEn,
    },
    {
      field: "baseYear",
      headerName: t("Base-Year"),
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      sortable: false,
      valueGetter: (value, row) => editData?.baseYear ?? "-",
    },
    ...fiveYearForecast.map((y) => {
      return {
        field: `${y}`,
        headerName: `${y}`,
        // headerAlign: "center",
        // align: "left",
        disableColumnMenu: true,
        minWidth: 140,
        editable: false,
        sortable: false,
        type: "number",
        headerAlign: "center",
        align: "center",
        renderCell(params) {
          let plan: number = 0;

          if (params.row._id !== "TOTAL") {
            // เอาค่าของ componentPlan มาแสดง
            plan =
              planSettingNormalization.componentPlans
                .find((c) => c.componentRef === params.id)
                ?.plans.find((p) => p.year === y)?.normalizedValue ?? 0;
          } else {
            // เอาค่าของ ghg
            plan =
              planSettingNormalization.emissionPlans.find((e) => e.year === y)
                ?.normalizedValue ?? 0;
          }

          return NumberFormatterUtils.numberFormat(plan) ?? "-";
        },
      } as GridColDef<EconomicIndicatorComponent>;
    }),
  ];

  useEffect(() => {
    if (editData) {
      setRows([
        ...editData.economicindicatorsRef.components,
        { _id: "TOTAL", nameEn: "total", nameTh: "total", unit: "total" },
      ]);
    }
  }, [editData, fiveYearForecast]);

  return (
    <Box display={"flex"} flexDirection={"column"} gap={"20px"} width={"100%"}>
      <LabelInput
        label={t("Normalization")}
        info={t("Normalization")}
        showRequired={false}
      />
      <AppDataGrid
        rows={rows}
        columns={columns}
        rowHeight={90}
        sx={{ ...tableStyle() }}
        getRowId={(row) => row._id}
        disableMultipleRowSelection={true}
        hideFooter
        autoHeight
      />
    </Box>
  );
};
